/**
 * H1 Sprint 3 / C6: 1 channel Realtime por agente (consolidado).
 *
 * Antes: 3 channels por agente activo
 *   - sf-crm-call-sync-${activeCrmCallId}     (UPDATE crm_calls por id, dinámico)
 *   - sf-crm-calls-insert-agent-${userId}     (INSERT crm_calls por agent_id)
 *   - call-center-agent-status-global         (UPDATE agent_status por user_id)
 *
 * A 1000 agentes = 3000 channels → satura límite de Supabase Realtime.
 *
 * Después: 1 channel `agent-rt-${tenantId}-${userId}` con 3 listeners.
 *   - INSERT crm_calls (agent_id=eq.userId)   → onCrmCallInsert
 *   - UPDATE crm_calls (agent_id=eq.userId)   → onCrmCallUpdate (filter por id activo client-side)
 *   - UPDATE agent_status (user_id=eq.userId) → onAgentStatusUpdate
 *
 * A 1000 agentes = 1000 channels.
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface CrmCallInsertRow {
  id: string;
  tenant_id: string;
  agent_id: string;
  destination_phone: string | null;
  call_status: string | null;
}

export interface CrmCallUpdateRow {
  id: string;
  tenant_id: string;
  agent_id: string | null;
  call_status: string | null;
}

export interface AgentStatusUpdateRow {
  tenant_id: string;
  user_id: string;
  status: string;
}

export interface AgentRealtimeHandlers {
  onCrmCallInsert?: (row: CrmCallInsertRow) => void;
  onCrmCallUpdate?: (row: CrmCallUpdateRow) => void;
  onAgentStatusUpdate?: (row: AgentStatusUpdateRow) => void;
}

export interface UseAgentRealtimeChannelResult {
  /** True cuando el channel completó SUBSCRIBED. C7 lo usa para apagar polling fallback. */
  isRealtimeConnected: boolean;
}

export function useAgentRealtimeChannel(
  tenantId: string | null | undefined,
  userId: string | null | undefined,
  handlers: AgentRealtimeHandlers,
): UseAgentRealtimeChannelResult {
  // Mantenemos handlers en ref para evitar re-suscribir el channel cada render.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);

  useEffect(() => {
    if (!tenantId || !userId) {
      setIsRealtimeConnected(false);
      return;
    }

    const channel = supabase
      .channel(`agent-rt-${tenantId}-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'crm_calls',
          filter: `agent_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Partial<CrmCallInsertRow> | undefined;
          if (!row || row.tenant_id !== tenantId) return;
          handlersRef.current.onCrmCallInsert?.(row as CrmCallInsertRow);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_calls',
          filter: `agent_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Partial<CrmCallUpdateRow> | undefined;
          if (!row || row.tenant_id !== tenantId) return;
          handlersRef.current.onCrmCallUpdate?.(row as CrmCallUpdateRow);
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'call_center_agent_status',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as Partial<AgentStatusUpdateRow> | undefined;
          if (!row || row.tenant_id !== tenantId) return;
          handlersRef.current.onAgentStatusUpdate?.(row as AgentStatusUpdateRow);
        },
      )
      .subscribe((status) => {
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    return () => {
      setIsRealtimeConnected(false);
      void supabase.removeChannel(channel);
    };
  }, [tenantId, userId]);

  return { isRealtimeConnected };
}
